/**
 * GMA Auth — Lightweight Google Sign-In + OCR for explorations pages.
 *
 * No gmath/Vue dependency. Uses the same localStorage keys as the main
 * gmath userStore so login state is shared across all pages.
 *
 * @module auth/gma-auth
 */

const GMA_SERVER = 'https://activities.graspablemath.com/api';
const GOOGLE_CLIENT_ID =
  '81925747312-vao5h4l5bf2c8cbfssff1bejaut2at9n.apps.googleusercontent.com';
const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const TOKEN_KEY = 'gma-auth-token';
const USER_KEY = 'gm-user-info';

// ── Token helpers ────────────────────────────────────────────────────────────

/** Decode a JWT's payload, or null if it isn't a readable token. */
export function decodeJwtPayload(token) {
  try {
    // JWTs are base64url ('-' and '_' standing in for '+' and '/', padding stripped),
    // but atob only accepts standard base64, so the raw segment can throw.
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

/** Get the stored GMA auth token, or null. */
export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY) || null;
}

/** Check if the user is logged in with a non-expired token. */
export function isLoggedIn() {
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  // 60-second buffer (same as userStore)
  return Date.now() / 1000 < payload.exp - 60;
}

/** Get stored user info ({ display_name, email }), or null. */
export function getUserInfo() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
}

/** Clear auth state. */
export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// ── Google Sign-In ───────────────────────────────────────────────────────────

function loadGsiScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  });
}

/**
 * Render a Google Sign-In button into the given container element.
 * Returns a promise that resolves with user info on successful sign-in.
 * Uses renderButton (reliable) instead of prompt/One Tap (FedCM issues).
 * @param {HTMLElement} containerEl - DOM element to render the button into
 * @returns {Promise<{ display_name: string, email: string }>}
 */
export function loginWithGoogle(containerEl) {
  return new Promise(async (resolve, reject) => {
    try {
      await loadGsiScript();
    } catch (err) {
      reject(err);
      return;
    }

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        if (!response.credential) {
          reject(new Error('Google Sign-In was cancelled'));
          return;
        }
        try {
          const userInfo = await exchangeGoogleToken(response.credential);
          resolve(userInfo);
        } catch (err) {
          reject(err);
        }
      },
    });

    if (containerEl) {
      google.accounts.id.renderButton(containerEl, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: Math.min(containerEl.offsetWidth || 300, 400),
        text: 'signin_with',
      });
    } else {
      // Fallback: create a temporary container and append it to body
      const tmp = document.createElement('div');
      tmp.style.cssText =
        'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:10000;background:#fff;padding:2rem;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.2);';
      const backdrop = document.createElement('div');
      backdrop.style.cssText =
        'position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:9999;';
      document.body.append(backdrop, tmp);
      google.accounts.id.renderButton(tmp, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });
      const cleanup = () => {
        backdrop.remove();
        tmp.remove();
      };
      backdrop.onclick = () => {
        cleanup();
        reject(new Error('Sign-in cancelled'));
      };
      // Clean up on resolve/reject
      const origResolve = resolve;
      const origReject = reject;
      resolve = (v) => {
        cleanup();
        origResolve(v);
      };
      reject = (e) => {
        cleanup();
        origReject(e);
      };
    }
  });
}

async function exchangeGoogleToken(googleCredential) {
  const response = await fetch(`${GMA_SERVER}/authentication`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      strategy: 'google-id-token',
      access_token: googleCredential,
      createNew: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`Auth failed: ${response.status}`);
  }

  const data = await response.json();

  if (!data.accessToken) {
    throw new Error('Auth failed: response contained no accessToken');
  }

  // Store the GMA JWT under the same key the gmath userStore uses, so login is shared.
  localStorage.setItem(TOKEN_KEY, data.accessToken);

  // Store user info for display
  const userInfo = {
    display_name: data.user?.displayName || data.user?.email || 'User',
    email: data.user?.email || '',
  };
  localStorage.setItem(USER_KEY, JSON.stringify(userInfo));

  return userInfo;
}

// ── OCR ──────────────────────────────────────────────────────────────────────

/**
 * Call the GMA OCR endpoint to detect math in an image.
 * @param {string} imageDataUrl - Base64 data URL (e.g., "data:image/png;base64,...")
 * @returns {Promise<string>} The detected LaTeX string
 * @throws {Error} If not logged in, or OCR fails
 */
export async function callOCR(imageDataUrl) {
  const token = getAuthToken();
  if (!token || !isLoggedIn()) {
    throw new Error('NOT_LOGGED_IN');
  }

  const response = await fetch(`${GMA_SERVER}/ocr/detect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ imageDataUrl }),
  });

  if (response.status === 401) throw new Error('OCR_UNAUTHORIZED');
  if (response.status === 429) throw new Error('OCR_LIMIT');
  if (!response.ok) throw new Error('OCR_FAILED');

  const data = await response.json();
  if (!data.latex) throw new Error('NO_LATEX');

  return data.latex;
}
